import { EMPTY_TEXT } from '../labels';
import { formatCompact } from '../format';
import { BAR_COLORS, scale, truncate } from './chart-utils';
import styles from '../styles.module.scss';

// charts/rank-bars.tsx — cột ngang (SPEC Studio mục 8.7.4, 8.7.7, 8.8).
export interface RankBarsRow {
  key: string;
  label: string;
  value: number;
  sub?: string;
}
export interface RankBarsProps {
  rows: RankBarsRow[]; // value = chênh lệch có dấu
  mode: 'UP' | 'DOWN';
  max?: number; // số dòng, mặc định 5
  formatValue?: (n: number) => string;
  emptyText?: string; // mặc định 'Chưa có dữ liệu'
  ariaLabel: string;
}

const WIDTH = 520;
const ROW_H = 40;
const PAD_Y = 6;
const LABEL_W = 176;
const VALUE_W = 118;
const BAR_H = 18;

/** Số có dấu gọn: +500 N / −600 N. */
const signedCompact = (n: number) => (n > 0 ? `+${formatCompact(n)}` : formatCompact(n));

/**
 * Top tăng / giảm theo chênh lệch có dấu. UP: chỉ dòng > 0, lớn nhất trước, màu successInk / successSoft;
 * DOWN: chỉ dòng < 0, giảm nhiều nhất trước, màu dangerInk / dangerSoft. Luôn kèm mũi tên và dấu +/−
 * (không chỉ dựa vào màu); số hiện ngay cạnh cột, không phụ thuộc rê chuột.
 */
const RankBars = ({
  rows,
  mode,
  max = 5,
  formatValue = signedCompact,
  emptyText = EMPTY_TEXT.chart,
  ariaLabel,
}: RankBarsProps) => {
  const list = rows
    .filter((r) => Number.isFinite(r.value) && (mode === 'UP' ? r.value > 0 : r.value < 0))
    .sort((a, b) => (mode === 'UP' ? b.value - a.value : a.value - b.value))
    .slice(0, Math.max(0, max));

  if (!list.length) {
    return (
      <div className={styles.chartEmpty} role="note" aria-label={ariaLabel}>
        {emptyText}
      </div>
    );
  }

  const maxAbs = Math.max(...list.map((r) => Math.abs(r.value)));
  const barMaxW = WIDTH - LABEL_W - VALUE_W - 8;
  const height = PAD_Y * 2 + list.length * ROW_H;
  const ink = mode === 'UP' ? BAR_COLORS.upInk : BAR_COLORS.downInk;
  const soft = mode === 'UP' ? BAR_COLORS.upSoft : BAR_COLORS.downSoft;
  const arrow = mode === 'UP' ? '▲' : '▼';

  return (
    <svg
      className={styles.chartSvg}
      viewBox={`0 0 ${WIDTH} ${height}`}
      width="100%"
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="xMidYMid meet"
    >
      <title>{ariaLabel}</title>
      {list.map((r, i) => {
        const y = PAD_Y + i * ROW_H;
        const w = Math.max(2, scale(Math.abs(r.value), maxAbs, barMaxW));
        const text = `${arrow} ${formatValue(r.value)}`;
        return (
          <g key={r.key}>
            <title>{`${r.label}${r.sub ? ` · ${r.sub}` : ''}: ${formatValue(r.value)}`}</title>
            <text x={0} y={y + (r.sub ? 15 : 22)} fontSize="12.5" fontWeight={600} fill={BAR_COLORS.value}>
              {truncate(r.label, 24)}
            </text>
            {r.sub && (
              <text x={0} y={y + 31} fontSize="11" fill={BAR_COLORS.axis}>
                {truncate(r.sub, 30)}
              </text>
            )}
            <rect
              x={LABEL_W}
              y={y + (ROW_H - BAR_H) / 2 - 2}
              width={w}
              height={BAR_H}
              rx={4}
              fill={soft}
              stroke={ink}
              strokeWidth={1}
            />
            <text
              x={LABEL_W + w + 8}
              y={y + ROW_H / 2 + 2}
              fontSize="12"
              fontWeight={600}
              fill={ink}
            >
              {text}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default RankBars;
